import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import StatusBadge from './StatusBadge';
import ActionButtons from './ActionButtons';
import CommentSection from './CommentSection';
import EditHistory from './EditHistory';

const ExpandableDetailRow = ({ 
  item, 
  type = 'assignment', 
  onEdit, 
  onDelete, 
  onStatusChange,
  onView,
  isExpanded,
  onToggleExpand
}) => {
  const [activeTab, setActiveTab] = useState('details');

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const renderAssignmentDetails = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Left Column - Main Details */}
      <div className="space-y-6">
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="FileText" size={20} className="mr-2 text-primary" />
            Assignment Information
          </h4>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm font-medium">Type</label>
                <p className="text-white font-medium">{item.assignment_type || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-white/60 text-sm font-medium">Pages</label>
                <p className="text-white font-medium">{item.pages || 0} pages</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm font-medium">Deadline</label>
                <p className="text-white font-medium">{formatDate(item.deadline)}</p>
              </div>
              <div>
                <label className="text-white/60 text-sm font-medium">Status</label>
                <StatusBadge status={item.status} size="sm" />
              </div>
            </div>
            {item.writer_name && (
              <div>
                <label className="text-white/60 text-sm font-medium">Writer</label>
                <p className="text-white font-medium">{item.writer_name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="ClipboardList" size={20} className="mr-2 text-secondary" />
            Requirements
          </h4>
          <div className="text-white/80 leading-relaxed">
            {item.requirements ? (
              <p>{item.requirements}</p>
            ) : (
              <p className="text-white/50 italic">No requirements specified</p>
            )}
          </div>
        </div>
      </div>

      {/* Right Column - Additional Info */}
      <div className="space-y-6">
        {/* Client Information */}
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="User" size={20} className="mr-2 text-accent" />
            Client Information
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm font-medium">Client</label>
              <p className="text-white font-medium">{item.client_name || 'Unknown Client'}</p>
            </div>
            {item.client_email && (
              <div>
                <label className="text-white/60 text-sm font-medium">Email</label>
                <p className="text-white font-medium">{item.client_email}</p>
              </div>
            )}
          </div>
        </div>

        {/* Attachments */}
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="Paperclip" size={20} className="mr-2 text-warning" />
            Attachments
          </h4>
          {item.attachments && item.attachments.length > 0 ? (
            <div className="space-y-2">
              {item.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="File" size={16} className="text-white/60" />
                    <span className="text-white/80 text-sm">{attachment.name}</span>
                  </div>
                  <button className="text-primary hover:text-primary/80 transition-colors">
                    <Icon name="Download" size={16} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/50 italic">No attachments</p>
          )}
        </div>

        {/* Progress Timeline */}
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="Clock" size={20} className="mr-2 text-success" />
            Timeline
          </h4>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <div>
                <p className="text-white font-medium text-sm">Created</p>
                <p className="text-white/60 text-xs">{formatDate(item.created_at)}</p>
              </div>
            </div>
            {item.updated_at && (
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-primary rounded-full"></div>
                <div>
                  <p className="text-white font-medium text-sm">Last Updated</p>
                  <p className="text-white/60 text-xs">{formatDate(item.updated_at)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderClassDetails = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Left Column */}
      <div className="space-y-6">
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="GraduationCap" size={20} className="mr-2 text-secondary" />
            Class Information
          </h4>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm font-medium">Level</label>
                <p className="text-white font-medium">{item.class_level || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-white/60 text-sm font-medium">Duration</label>
                <p className="text-white font-medium">{item.duration || 'Not specified'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm font-medium">Start Date</label>
                <p className="text-white font-medium">{formatDate(item.start_date)}</p>
              </div>
              <div>
                <label className="text-white/60 text-sm font-medium">End Date</label>
                <p className="text-white font-medium">{formatDate(item.end_date)}</p>
              </div>
            </div>
            <div>
              <label className="text-white/60 text-sm font-medium">Status</label>
              <StatusBadge status={item.status} size="sm" />
            </div>
          </div>
        </div>

        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="BookOpen" size={20} className="mr-2 text-primary" />
            Description
          </h4>
          <div className="text-white/80 leading-relaxed">
            {item.description ? (
              <p>{item.description}</p>
            ) : (
              <p className="text-white/50 italic">No description provided</p>
            )}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-6">
        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="Users" size={20} className="mr-2 text-accent" />
            Participants
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm font-medium">Student</label>
              <p className="text-white font-medium">{item.client_name || 'Unknown Student'}</p>
            </div>
            {item.instructor && (
              <div>
                <label className="text-white/60 text-sm font-medium">Instructor</label>
                <p className="text-white font-medium">{item.instructor}</p>
              </div>
            )}
          </div>
        </div>

        <div className="glass-card p-6 rounded-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Icon name="MapPin" size={20} className="mr-2 text-warning" />
            Session Details
          </h4>
          <div className="space-y-3">
            {item.meeting_link && (
              <div>
                <label className="text-white/60 text-sm font-medium">Meeting Link</label>
                <a 
                  href={item.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 transition-colors text-sm"
                >
                  Join Session
                </a>
              </div>
            )}
            {item.location && (
              <div>
                <label className="text-white/60 text-sm font-medium">Location</label>
                <p className="text-white font-medium text-sm">{item.location}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const detailTabs = [
    { id: 'details', label: 'Details', icon: 'Info' },
    { id: 'comments', label: 'Comments', icon: 'MessageSquare' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ 
        opacity: isExpanded ? 1 : 0, 
        height: isExpanded ? 'auto' : 0 
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="overflow-hidden"
    >
      {isExpanded && (
        <div className="border-t border-white/10 bg-gradient-to-b from-white/5 to-transparent">
          <div className="p-8">
            {/* Header with Actions */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                  <Icon name={type === 'assignment' ? 'FileText' : 'GraduationCap'} size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {item.assignment_title || item.class_title || item.class_name || 'Untitled'}
                  </h3>
                  <p className="text-white/60 capitalize">{type} Details</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <ActionButtons 
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onStatusChange={onStatusChange}
                  currentStatus={item.status}
                  size="lg"
                />
                <button
                  onClick={onToggleExpand}
                  className="p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-8 bg-white/5 rounded-xl p-1">
              {detailTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 flex-1 justify-center ${
                    activeTab === tab.id
                      ? 'bg-primary text-white shadow-lg'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon name={tab.icon} size={18} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'details' && (
                  type === 'assignment' ? renderAssignmentDetails() : renderClassDetails()
                )}
                
                {activeTab === 'comments' && (
                  <div className="glass-card p-6 rounded-xl">
                    <CommentSection 
                      itemId={item.id} 
                      itemType={type}
                    />
                  </div>
                )}
                
                {activeTab === 'history' && (
                  <div className="glass-card p-6 rounded-xl">
                    <EditHistory 
                      itemId={item.id} 
                      itemType={type}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ExpandableDetailRow;